import { GaugeSettingsSchema, type GaugeSettingsType } from './gauge-schema'
import { ZodError } from 'zod'

export class GaugeImportError extends Error {
  constructor(message: string, public details?: unknown) {
    super(message)
    this.name = 'GaugeImportError'
    this.details = details
    Object.setPrototypeOf(this, GaugeImportError.prototype)
  }
}

export const exportGaugeConfig = (config: GaugeSettingsType): string => {
  try {
    // Validate the config before exporting
    GaugeSettingsSchema.parse(config)
    return JSON.stringify(config, null, 2)
  } catch (error) {
    console.error('Error exporting gauge config:', error)
    if (error instanceof ZodError) {
      throw new GaugeImportError('Invalid gauge configuration for export', {
        zodError: error.format(),
      })
    }
    throw new GaugeImportError('Failed to export gauge configuration')
  }
}

export const importGaugeConfig = (jsonString: string): GaugeSettingsType => {
  try {
    // Parse JSON
    const parsedJson = JSON.parse(jsonString)

    // Validate against schema
    const result = GaugeSettingsSchema.safeParse(parsedJson)

    if (!result.success) {
      console.error('Validation errors:', result.error.format())
      throw new GaugeImportError('Invalid gauge configuration format', {
        zodError: result.error.format(),
      })
    }

    return result.data
  } catch (error) {
    console.error('Error importing gauge config:', error)
    if (error instanceof SyntaxError) {
      throw new GaugeImportError('Invalid JSON format')
    }
    if (error instanceof GaugeImportError) {
      throw error
    }
    throw new GaugeImportError('Failed to import gauge configuration')
  }
}

export const downloadGaugeConfig = (config: GaugeSettingsType) => {
  const configString = exportGaugeConfig(config)
  const blob = new Blob([configString], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `gauge-config-${new Date().toISOString()}.json`
  document.body.appendChild(a)
  a.click()

  // Cleanup
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const readGaugeConfigFile = (file: File): Promise<GaugeSettingsType> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const content = event.target?.result
        if (typeof content !== 'string') {
          throw new GaugeImportError('Failed to read file content')
        }
        resolve(importGaugeConfig(content))
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => {
      reject(new GaugeImportError('Failed to read file'))
    }

    reader.readAsText(file)
  })
}
